import React, { useEffect, useState } from "react";
import api from "../services/api";
import Swal from "sweetalert2";

const Vehiculos = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [form, setForm] = useState({
    placa: "",
    marca: "",
    modelo: "",
    año: "",
    color: "",
    tipo: "sedan",
    costo_diario: "",
    costo_semanal: "",
    costo_mensual: "",
    kilometraje: "",
    estado: "disponible"
  });
  const [editando, setEditando] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");

  // ═══════════════════════════════════════════════════════════════════
  // 📥 CARGAR VEHÍCULOS
  // ═══════════════════════════════════════════════════════════════════
  const cargarVehiculos = async () => {
    try {
      setCargando(true);
      const res = await api.get("/rencar/vehiculos");
      setVehiculos(res.data.vehiculos || []);
    } catch (error) {
      console.error("❌ Error cargando vehículos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los vehículos"
      });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarVehiculos();
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // 💾 GUARDAR O ACTUALIZAR
  // ═══════════════════════════════════════════════════════════════════
  const guardar = async () => {
    // Validaciones
    if (!form.placa || !form.marca || !form.modelo || !form.costo_diario) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Placa, marca, modelo y costo diario son obligatorios"
      });
      return;
    }

    try {
      let response;
      if (editando) {
        response = await api.put(`/rencar/vehiculos/${editando}`, form);
      } else {
        response = await api.post("/rencar/vehiculos", form);
      }

      if (response.data.ok) {
        Swal.fire({
          icon: "success",
          title: editando ? "✅ Actualizado" : "✅ Registrado",
          text: `Vehículo ${editando ? "actualizado" : "registrado"} correctamente`,
          timer: 1500,
          showConfirmButton: false
        });

        limpiarFormulario();
        setMostrarFormulario(false);
        await cargarVehiculos();
      }
    } catch (error) {
      console.error("❌ Error guardando vehículo:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.msg || "No se pudo guardar el vehículo"
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // ✏️ EDITAR
  // ═══════════════════════════════════════════════════════════════════
  const editar = (vehiculo) => {
    setForm({
      placa: vehiculo.placa,
      marca: vehiculo.marca,
      modelo: vehiculo.modelo,
      año: vehiculo.año || "",
      color: vehiculo.color || "",
      tipo: vehiculo.tipo || "sedan",
      costo_diario: vehiculo.costo_diario,
      costo_semanal: vehiculo.costo_semanal || "",
      costo_mensual: vehiculo.costo_mensual || "",
      kilometraje: vehiculo.kilometraje || "",
      estado: vehiculo.estado
    });
    setEditando(vehiculo.id);
    setMostrarFormulario(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ═══════════════════════════════════════════════════════════════════
  // 🗑️ ELIMINAR
  // ═══════════════════════════════════════════════════════════════════
  const eliminar = async (id, placa) => {
    const result = await Swal.fire({
      title: "¿Eliminar vehículo?",
      html: `Se eliminará el vehículo <strong>${placa}</strong><br>Esta acción no se puede deshacer`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    });

    if (!result.isConfirmed) return;

    try {
      const res = await api.delete(`/rencar/vehiculos/${id}`);
      if (res.data.ok) {
        Swal.fire({
          icon: "success",
          title: "Eliminado",
          text: "Vehículo eliminado correctamente",
          timer: 1500,
          showConfirmButton: false
        });
        cargarVehiculos();
      }
    } catch (error) {
      console.error("❌ Error eliminando vehículo:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo eliminar el vehículo"
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // 🧹 LIMPIAR FORMULARIO
  // ═══════════════════════════════════════════════════════════════════
  const limpiarFormulario = () => {
    setForm({
      placa: "",
      marca: "",
      modelo: "",
      año: "",
      color: "",
      tipo: "sedan",
      costo_diario: "",
      costo_semanal: "",
      costo_mensual: "",
      kilometraje: "",
      estado: "disponible"
    });
    setEditando(null);
  };

  // ═══════════════════════════════════════════════════════════════════
  // 🔍 FILTRAR VEHÍCULOS
  // ═══════════════════════════════════════════════════════════════════
  const vehiculosFiltrados = vehiculos.filter((v) => {
    const coincideBusqueda =
      v.placa.toLowerCase().includes(busqueda.toLowerCase()) ||
      v.marca.toLowerCase().includes(busqueda.toLowerCase()) ||
      v.modelo.toLowerCase().includes(busqueda.toLowerCase());

    const coincideEstado =
      filtroEstado === "todos" || v.estado === filtroEstado;

    return coincideBusqueda && coincideEstado;
  });

  // ═══════════════════════════════════════════════════════════════════
  // 📊 ESTADÍSTICAS
  // ═══════════════════════════════════════════════════════════════════
  const stats = {
    total: vehiculos.length,
    disponibles: vehiculos.filter((v) => v.estado === "disponible").length,
    rentados: vehiculos.filter((v) => v.estado === "rentado").length,
    mantenimiento: vehiculos.filter((v) => v.estado === "mantenimiento").length
  };

  // ═══════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════
  if (cargando) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Cargando vehículos...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🚗 Gestión de Vehículos</h1>
          <p style={styles.subtitle}>Administra la flota de vehículos disponibles</p>
        </div>
        <button
          style={styles.btnPrimary}
          onClick={() => {
            limpiarFormulario();
            setMostrarFormulario(!mostrarFormulario);
          }}
        >
          {mostrarFormulario ? "❌ Cancelar" : "➕ Nuevo Vehículo"}
        </button>
      </div>

      {/* ESTADÍSTICAS */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>🚙</div>
          <div>
            <div style={styles.statValue}>{stats.total}</div>
            <div style={styles.statLabel}>Total Flota</div>
          </div>
        </div>
        <div style={{...styles.statCard, borderLeft: '4px solid #16a34a'}}>
          <div style={{...styles.statIcon, background: '#d1fae5', color: '#16a34a'}}>✅</div>
          <div>
            <div style={styles.statValue}>{stats.disponibles}</div>
            <div style={styles.statLabel}>Disponibles</div>
          </div>
        </div>
        <div style={{...styles.statCard, borderLeft: '4px solid #f59e0b'}}>
          <div style={{...styles.statIcon, background: '#fef3c7', color: '#f59e0b'}}>🔑</div>
          <div>
            <div style={styles.statValue}>{stats.rentados}</div>
            <div style={styles.statLabel}>Rentados</div>
          </div>
        </div>
        <div style={{...styles.statCard, borderLeft: '4px solid #dc2626'}}>
          <div style={{...styles.statIcon, background: '#fee2e2', color: '#dc2626'}}>🔧</div>
          <div>
            <div style={styles.statValue}>{stats.mantenimiento}</div>
            <div style={styles.statLabel}>Mantenimiento</div>
          </div>
        </div>
      </div>

      {/* FORMULARIO */}
      {mostrarFormulario && (
        <div style={styles.formCard}>
          <h3 style={styles.formTitle}>
            {editando ? "✏️ Editar Vehículo" : "➕ Nuevo Vehículo"}
          </h3>

          <div style={styles.formGrid}>
            {/* Placa */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Placa *</label>
              <input
                style={styles.input}
                placeholder="Ej: A123456"
                value={form.placa}
                onChange={(e) =>
                  setForm({ ...form, placa: e.target.value.toUpperCase() })
                }
              />
            </div>

            {/* Marca */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Marca *</label>
              <input
                style={styles.input}
                placeholder="Ej: Toyota"
                value={form.marca}
                onChange={(e) => setForm({ ...form, marca: e.target.value })}
              />
            </div>

            {/* Modelo */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Modelo *</label>
              <input
                style={styles.input}
                placeholder="Ej: Corolla"
                value={form.modelo}
                onChange={(e) => setForm({ ...form, modelo: e.target.value })}
              />
            </div>

            {/* Año */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Año</label>
              <input
                style={styles.input}
                type="number"
                placeholder="2024"
                value={form.año}
                onChange={(e) => setForm({ ...form, año: e.target.value })}
              />
            </div>

            {/* Color */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Color</label>
              <input
                style={styles.input}
                placeholder="Ej: Blanco"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
              />
            </div>

            {/* Tipo */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Tipo</label>
              <select
                style={styles.input}
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              >
                <option value="sedan">Sedán</option>
                <option value="suv">SUV</option>
                <option value="pickup">Pickup</option>
                <option value="van">Van</option>
                <option value="deportivo">Deportivo</option>
                <option value="compacto">Compacto</option>
              </select>
            </div>

            {/* Costo Diario */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Costo Diario (USD) *</label>
              <input
                style={styles.input}
                type="number"
                placeholder="1500"
                value={form.costo_diario}
                onChange={(e) =>
                  setForm({ ...form, costo_diario: e.target.value })
                }
              />
            </div>

            {/* Costo Semanal */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Costo Semanal (USD)</label>
              <input
                style={styles.input}
                type="number"
                placeholder="9000"
                value={form.costo_semanal}
                onChange={(e) =>
                  setForm({ ...form, costo_semanal: e.target.value })
                }
              />
            </div>

            {/* Costo Mensual */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Costo Mensual (USD)</label>
              <input
                style={styles.input}
                type="number"
                placeholder="30000"
                value={form.costo_mensual}
                onChange={(e) =>
                  setForm({ ...form, costo_mensual: e.target.value })
                }
              />
            </div>

            {/* Kilometraje */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Kilometraje Actual</label>
              <input
                style={styles.input}
                type="number"
                placeholder="50000"
                value={form.kilometraje}
                onChange={(e) =>
                  setForm({ ...form, kilometraje: e.target.value })
                }
              />
            </div>

            {/* Estado */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Estado</label>
              <select
                style={styles.input}
                value={form.estado}
                onChange={(e) => setForm({ ...form, estado: e.target.value })}
              >
                <option value="disponible">Disponible</option>
                <option value="rentado">Rentado</option>
                <option value="mantenimiento">Mantenimiento</option>
              </select>
            </div>
          </div>

          <div style={styles.formActions}>
            <button style={styles.btnSecondary} onClick={() => {
              limpiarFormulario();
              setMostrarFormulario(false);
            }}>
              Cancelar
            </button>
            <button style={styles.btnPrimary} onClick={guardar}>
              {editando ? "💾 Actualizar" : "🚀 Registrar"}
            </button>
          </div>
        </div>
      )}

      {/* FILTROS */}
      <div style={styles.filtersCard}>
        <div style={styles.filtersGrid}>
          <div style={styles.searchBox}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              style={styles.searchInput}
              placeholder="Buscar por placa, marca o modelo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <select
            style={styles.filterSelect}
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="todos">📋 Todos los estados</option>
            <option value="disponible">✅ Disponibles</option>
            <option value="rentado">🔑 Rentados</option>
            <option value="mantenimiento">🔧 Mantenimiento</option>
          </select>
        </div>
      </div>

      {/* TABLA */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>Flota de Vehículos</h3>
          <span style={styles.badge}>
            {vehiculosFiltrados.length} vehículo(s)
          </span>
        </div>

        {vehiculosFiltrados.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🚗</div>
            <p style={styles.emptyText}>
              {busqueda || filtroEstado !== "todos"
                ? "No se encontraron vehículos con los filtros aplicados"
                : "No hay vehículos registrados. ¡Agrega el primero!"}
            </p>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Placa</th>
                  <th style={styles.th}>Vehículo</th>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Año</th>
                  <th style={styles.th}>Color</th>
                  <th style={styles.th}>Costo/Día</th>
                  <th style={styles.th}>KM</th>
                  <th style={styles.th}>Estado</th>
                  <th style={{...styles.th, textAlign: 'right'}}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {vehiculosFiltrados.map((v) => (
                  <tr key={v.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.placaBadge}>{v.placa}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.vehiculoInfo}>
                        <div style={styles.vehiculoNombre}>
                          {v.marca} {v.modelo}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{v.tipo || '-'}</td>
                    <td style={styles.td}>{v.año || '-'}</td>
                    <td style={styles.td}>{v.color || '-'}</td>
                    <td style={styles.td}>
                      <span style={styles.precio}>
                        USD ${parseFloat(v.costo_diario || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {v.kilometraje ? v.kilometraje.toLocaleString() + ' km' : '-'}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.estadoBadge,
                        ...(v.estado === 'disponible' ? styles.estadoDisponible :
                            v.estado === 'rentado' ? styles.estadoRentado :
                            styles.estadoMantenimiento)
                      }}>
                        {v.estado === 'disponible' ? '✅ Disponible' :
                         v.estado === 'rentado' ? '🔑 Rentado' :
                         '🔧 Mantenimiento'}
                      </span>
                    </td>
                    <td style={{...styles.td, textAlign: 'right'}}>
                      <div style={styles.actions}>
                        <button
                          style={styles.btnEdit}
                          onClick={() => editar(v)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          style={styles.btnDelete}
                          onClick={() => eliminar(v.id, v.placa)}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ANIMACIONES */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "calc(100vh - 70px)",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  loadingText: {
    marginTop: "16px",
    color: "#64748b",
    fontSize: "16px"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px"
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0
  },
  subtitle: {
    fontSize: "16px",
    color: "#64748b",
    margin: "4px 0 0 0"
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    marginBottom: "24px"
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid #3b82f6",
    display: "flex",
    alignItems: "center",
    gap: "16px"
  },
  statIcon: {
    width: "56px",
    height: "56px",
    background: "#dbeafe",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    color: "#3b82f6"
  },
  statValue: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b"
  },
  statLabel: {
    fontSize: "14px",
    color: "#64748b",
    marginTop: "4px"
  },
  formCard: {
    background: "white",
    padding: "24px",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0",
    marginBottom: "24px"
  },
  formTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "20px"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    marginBottom: "20px"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#334155"
  },
  input: {
    padding: "10px 14px",
    fontSize: "14px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },
  formActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end"
  },
  btnPrimary: {
    padding: "12px 24px",
    background: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)"
  },
  btnSecondary: {
    padding: "12px 24px",
    background: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  filtersCard: {
    background: "white",
    padding: "16px 20px",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0",
    marginBottom: "24px"
  },
  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "12px",
    alignItems: "center"
  },
  searchBox: {
    position: "relative",
    display: "flex",
    alignItems: "center"
  },
  searchIcon: {
    position: "absolute",
    left: "14px",
    fontSize: "18px",
    color: "#94a3b8"
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px 10px 44px",
    fontSize: "14px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    outline: "none"
  },
  filterSelect: {
    padding: "10px 14px",
    fontSize: "14px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    outline: "none",
    cursor: "pointer",
    minWidth: "200px"
  },
  tableCard: {
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0",
    overflow: "hidden"
  },
  tableHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  tableTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1e293b",
    margin: 0
  },
  badge: {
    padding: "6px 12px",
    background: "#f1f5f9",
    color: "#475569",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    padding: "14px 16px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #e2e8f0",
    background: "#f8fafc"
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.2s"
  },
  td: {
    padding: "16px",
    fontSize: "14px",
    color: "#334155"
  },
  placaBadge: {
    padding: "6px 12px",
    background: "#1e293b",
    color: "white",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "0.5px"
  },
  vehiculoInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px"
  },
  vehiculoNombre: {
    fontWeight: "600",
    color: "#1e293b"
  },
  precio: {
    fontWeight: "600",
    color: "#16a34a"
  },
  estadoBadge: {
    padding: "6px 12px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    display: "inline-block"
  },
  estadoDisponible: {
    background: "#d1fae5",
    color: "#16a34a"
  },
  estadoRentado: {
    background: "#fef3c7",
    color: "#f59e0b"
  },
  estadoMantenimiento: {
    background: "#fee2e2",
    color: "#dc2626"
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end"
  },
  btnEdit: {
    padding: "8px 12px",
    background: "#fef3c7",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    transition: "all 0.2s"
  },
  btnDelete: {
    padding: "8px 12px",
    background: "#fee2e2",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    transition: "all 0.2s"
  },
  emptyState: {
    padding: "60px 20px",
    textAlign: "center"
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px",
    opacity: 0.5
  },
  emptyText: {
    fontSize: "16px",
    color: "#64748b",
    margin: 0
  }
};

export default Vehiculos;